import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import * as authApi from "./authApi";
import type { UserProfile } from "./authApi";

const REFRESH_INTERVAL_MS = 14 * 60 * 1000; // 14 minutes (before 15-min access token expiry)

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isEmailVerified: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    displayName: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isEmailVerified: false,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
  refreshUser: async () => {}
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const startRefreshTimer = useCallback(() => {
    clearRefreshTimer();
    refreshTimerRef.current = setInterval(() => {
      authApi.refreshTokens();
    }, REFRESH_INTERVAL_MS);
  }, [clearRefreshTimer]);

  const setAuthenticated = useCallback(
    (profile: UserProfile) => {
      setUser(profile);
      startRefreshTimer();
    },
    [startRefreshTimer]
  );

  const clearAuth = useCallback(() => {
    setUser(null);
    clearRefreshTimer();
  }, [clearRefreshTimer]);

  // Restore session on mount
  useEffect(() => {
    let cancelled = false;
    authApi
      .getMe()
      .then((profile) => {
        if (!cancelled) setAuthenticated(profile);
      })
      .catch(() => {
        if (!cancelled) clearAuth();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setAuthenticated, clearAuth]);

  // Cleanup timer on unmount
  useEffect(() => clearRefreshTimer, [clearRefreshTimer]);

  const login = useCallback(
    async (email: string, password: string) => {
      const profile = await authApi.login(email, password);
      setAuthenticated(profile);
    },
    [setAuthenticated]
  );

  const signup = useCallback(
    async (email: string, password: string, displayName: string) => {
      const profile = await authApi.signup(email, password, displayName);
      setAuthenticated(profile);
    },
    [setAuthenticated]
  );

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {});
    clearAuth();
  }, [clearAuth]);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await authApi.getMe();
      setUser(profile);
    } catch {
      // If refresh fails, don't clear auth — stale data is better than logging out
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    isEmailVerified: user?.emailVerified ?? false,
    login,
    signup,
    logout,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
